import { useEffect, useState, type FormEvent } from "react";
import { PageHeader } from "./design-system";
import { Check } from "lucide-react";
import { apiFetch } from "./lib/auth";
import { btnPrimary } from "./admin-ui";
import { InterestsSkeleton } from "./loading";
import { useToast } from "./toast";

// Post-purchase interests survey (UNB2-169). The scheduled email links here.
const OPTIONS = [
  { key: "app", label: "Una app" },
  { key: "web", label: "Una página o tienda" },
  { key: "juego", label: "Un juego" },
  { key: "automatizacion", label: "Automatizaciones / IA" },
  { key: "otro", label: "Otra cosa" },
] as const;

const LEVELS = [
  { key: "beginner", label: "Recién empiezo" },
  { key: "intermediate", label: "Algo de experiencia" },
  { key: "advanced", label: "Con experiencia" },
] as const;

type Profile = {
  interests: string[] | null;
  experienceLevel: string | null;
};

export function InterestsForm() {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [interests, setInterests] = useState<string[]>([]);
  const [level, setLevel] = useState<string>("");

  useEffect(() => {
    apiFetch<{ profile: Profile | null }>("/api/account/")
      .then((data) => {
        setInterests(data.profile?.interests ?? []);
        setLevel(data.profile?.experienceLevel ?? "");
      })
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, []);

  const toggle = (key: string) =>
    setInterests((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!interests.length || !level) return;
    setSaving(true);
    try {
      await apiFetch("/api/account/interests", {
        method: "PUT",
        body: JSON.stringify({ interests, experienceLevel: level }),
      });
      setDone(true);
      toast("¡Gracias! Vamos a usarlo para recomendarte lo próximo.");
    } catch {
      toast("No pudimos guardar. Prueba de nuevo.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <InterestsSkeleton />;

  if (done)
    return (
      <div className="border-ink bg-bone shadow-brutal border-surface mx-auto max-w-xl p-8 text-center">
        <div className="border-ink bg-acid border-surface mx-auto grid h-14 w-14 place-items-center">
          <Check size={28} />
        </div>
        <h1 className="font-display mt-5 text-3xl font-extrabold">¡Listo!</h1>
        <p className="text-ink-soft mt-2">
          Gracias por contarnos. Con esto te vamos a recomendar por dónde
          seguir.
        </p>
      </div>
    );

  return (
    <div className="mx-auto max-w-xl">
      <PageHeader
        kicker="Cuéntanos sobre ti"
        title="¿Qué quieres crear?"
        description="Toma 30 segundos y nos ayuda a recomendarte los cursos y el contenido pensados para lo tuyo."
        doodle="question-yellow"
        tone="bubble"
      />

      <form onSubmit={submit} className="mt-8">
        <fieldset>
          <legend className="font-mono text-xs font-bold uppercase tracking-wider">
            Lo que quieres construir (elige las que quieras)
          </legend>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {OPTIONS.map((option) => {
              const checked = interests.includes(option.key);
              return (
                <button
                  type="button"
                  key={option.key}
                  onClick={() => toggle(option.key)}
                  aria-pressed={checked}
                  className={`border-ink border-surface flex cursor-pointer items-center gap-3 px-4 py-3 text-left transition-shadow ${checked ? "bg-acid shadow-brutal" : "bg-paper hover:shadow-brutal"}`}
                >
                  <span
                    className={`border-ink border-chip grid h-5 w-5 shrink-0 place-items-center ${checked ? "bg-ink text-acid" : "bg-paper"}`}
                  >
                    {checked && <Check size={13} />}
                  </span>
                  <span className="font-bold">{option.label}</span>
                </button>
              );
            })}
          </div>
        </fieldset>

        <fieldset className="mt-6">
          <legend className="font-mono text-xs font-bold uppercase tracking-wider">
            Tu nivel
          </legend>
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            {LEVELS.map((option) => {
              const active = level === option.key;
              return (
                <button
                  type="button"
                  key={option.key}
                  onClick={() => setLevel(option.key)}
                  aria-pressed={active}
                  className={`border-ink border-surface cursor-pointer px-4 py-3 text-center font-bold transition-shadow ${active ? "bg-acid shadow-brutal" : "bg-paper hover:shadow-brutal"}`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </fieldset>

        <button
          type="submit"
          disabled={saving || !interests.length || !level}
          className={`${btnPrimary} mt-8 w-full py-3 disabled:cursor-not-allowed disabled:opacity-40`}
        >
          {saving ? "Guardando…" : "Guardar"}
        </button>
      </form>
    </div>
  );
}
